import { ApolloError } from '@apollo/client';
import { GraphQLError } from 'graphql';

export interface CustomGraphQLError extends GraphQLError {
  code?: string;
}

export enum HomecomingError {
  ResourceNotFound = 'ResourceNotFound',
  ResourceForbidden = 'ResourceForbidden',
  TokenInvalid = 'TokenInvalid',
}

export const matchHomecomingError = (
  err: ApolloError,
  matchError: HomecomingError,
) => {
  // Check all GraphQL errors, not just the first one
  return (
    err?.graphQLErrors?.find(
      (e) => (e as CustomGraphQLError)?.code === matchError,
    ) ?? null
  );
};

export const isAbortError = (err: ApolloError) => {
  return err.networkError?.name === 'AbortError';
};

/**
 * The backend signals an expired/invalid session either with the
 * `TokenInvalid` GraphQL error code or, in some flows, with a raw
 * `"Token is expired"` message and no machine-readable code. The Apollo
 * errorLink handles the coded case by clearing the session and redirecting
 * to login, but a raw expiry surfacing in a component catch block is the same
 * expected, recoverable condition — not an application defect.
 */
const TOKEN_EXPIRED_MESSAGE = 'Token is expired';

export const isExpiredTokenError = (
  err: ApolloError | null | undefined,
): boolean => {
  if (!err) {
    return false;
  }

  if (
    (err.graphQLErrors ?? []).some(
      (graphQLError) =>
        (graphQLError as CustomGraphQLError)?.code ===
          HomecomingError.TokenInvalid ||
        graphQLError?.message === TOKEN_EXPIRED_MESSAGE,
    )
  ) {
    return true;
  }

  return err.message === TOKEN_EXPIRED_MESSAGE;
};

/**
 * Detects a transient/network-level failure — the same condition Apollo's
 * retryLink already treats as retryable (see apolloHelpers.ts): the browser
 * could not complete the request ("Failed to fetch", i.e. offline / server
 * unreachable / request rejected by the gateway, e.g. an oversized upload) or
 * the request was aborted.
 *
 * These are environmental/transient conditions, not application bugs, so they
 * should be surfaced to the user (with actionable guidance) but not reported to
 * Sentry as exceptions.
 *
 * See Sentry issue WEB-95.
 */
export const isNetworkError = (err: ApolloError | null | undefined) => {
  if (!err) {
    return false;
  }

  const networkError = err.networkError as {
    name?: string;
    message?: string;
  } | null;
  if (!networkError) {
    return false;
  }

  return (
    networkError.name === 'AbortError' ||
    networkError.message === 'Failed to fetch' ||
    networkError.message ===
      'NetworkError when attempting to fetch resource.' ||
    networkError.message === 'Load failed'
  );
};

/**
 * GraphQL error codes that represent an *expected* server outcome rather than
 * a bug — e.g. a provider requesting a program assessment they don't have
 * access to (ResourceForbidden) or that doesn't exist (ResourceNotFound).
 *
 * These should still be surfaced to the user as a normal error state, but they
 * must not be reported to Sentry as exceptions: they are the backend correctly
 * rejecting the request, not an application error. Reporting them floods Sentry
 * with non-actionable auth/lookup rejections.
 *
 * See Sentry issue WEB-87.
 */
export const EXPECTED_GRAPHQL_ERROR_CODES: ReadonlySet<HomecomingError> =
  new Set([
    HomecomingError.ResourceForbidden,
    HomecomingError.ResourceNotFound,
  ]);

export const isExpectedGraphQLError = (err: ApolloError | null | undefined) => {
  if (!err) {
    return false;
  }

  const errors = err.graphQLErrors ?? [];
  if (errors.length === 0) {
    return false;
  }

  return errors.every((graphQLError) => {
    const code = (graphQLError as CustomGraphQLError)?.code;
    return !!code && EXPECTED_GRAPHQL_ERROR_CODES.has(code as HomecomingError);
  });
};

export const getGraphQLErrorMessage = (
  err: unknown,
  fallback = 'Something went wrong.',
): string => {
  if (err instanceof ApolloError) {
    return err.graphQLErrors?.[0]?.message || fallback;
  }
  return fallback;
};

/**
 * Detects an Auth0 "invalid state" / "missing transaction" failure from
 * `handleRedirectCallback`. This happens when the transaction/state nonce stored
 * at the start of the login flow is no longer present by the time the callback
 * runs — e.g. the callback URL was opened in a different browser/tab, ITP
 * blocked the cookie, or the callback already ran once and consumed the state.
 *
 * Such failures are recoverable: kicking off a fresh login creates a new
 * transaction, rather than looping back through a broken callback.
 *
 * See Sentry issue WEB-9F.
 */
export const isAuth0InvalidStateError = (error: unknown): boolean => {
  if (!error) {
    return false;
  }

  const err = error as Record<string, unknown> & { message?: unknown };
  const message =
    typeof err?.message === 'string' ? err.message.toLowerCase() : '';
  const errorCode =
    typeof err?.error === 'string' ? err.error.toLowerCase() : '';
  const errorDescription =
    typeof err?.error_description === 'string'
      ? err.error_description.toLowerCase()
      : '';

  // Auth0 signals a missing/invalid transaction nonce in a few ways.
  return (
    errorCode === 'missing_transaction' ||
    message.includes('invalid state') ||
    message.includes('missing transaction') ||
    errorDescription.includes('invalid state') ||
    errorDescription.includes('state mismatch')
  );
};
